import uiConfig from '@tenant/ui-config'

export type ProfileMode = 'full' | 'light' | 'off'

export type ProfileSectionKey =
  | 'identity'
  | 'about'
  | 'contact'
  | 'location'
  | 'headline'
  | 'skills'
  | 'experience'
  | 'papers'
  | 'education'
  | 'certifications'
  | 'banner'
  | 'publicShare'
  | 'openToWork'

export type ProfileSections = Record<ProfileSectionKey, boolean>

export interface ResolvedProfileFeature {
  enabled: boolean
  mode: ProfileMode
  sections: ProfileSections
}

export type ProfileFeatureConfig = {
  enabled?: boolean
  mode?: ProfileMode
  sections?: Partial<ProfileSections>
  byFocus?: Record<
    string,
    {
      enabled?: boolean
      mode?: ProfileMode
      sections?: Partial<ProfileSections>
    }
  >
}

const FULL_SECTIONS: ProfileSections = {
  identity: true,
  about: true,
  contact: true,
  location: true,
  headline: true,
  skills: true,
  experience: true,
  papers: true,
  education: true,
  certifications: true,
  banner: true,
  publicShare: true,
  openToWork: false,
}

const LIGHT_SECTIONS: ProfileSections = {
  identity: true,
  about: true,
  contact: true,
  location: true,
  headline: false,
  skills: false,
  experience: false,
  papers: false,
  education: false,
  certifications: false,
  banner: false,
  publicShare: false,
  openToWork: false,
}

function mergeSections(
  base: ProfileSections,
  override?: Partial<ProfileSections>,
): ProfileSections {
  if (!override) return { ...base }
  return { ...base, ...override }
}

/**
 * Pure resolver for profile mode/sections (testable without Vite env).
 * Missing config → full mode (backward compatible with employment tenants).
 */
export function resolveProfileFeatureFromConfig(
  raw: ProfileFeatureConfig | undefined,
  focus?: string | null,
): ResolvedProfileFeature {
  if (!raw) {
    return { enabled: true, mode: 'full', sections: { ...FULL_SECTIONS } }
  }

  if (raw.enabled === false) {
    return { enabled: false, mode: 'off', sections: { ...LIGHT_SECTIONS } }
  }

  const focusOverride = focus ? raw.byFocus?.[focus] : undefined
  if (focusOverride?.enabled === false) {
    return { enabled: false, mode: 'off', sections: { ...LIGHT_SECTIONS } }
  }

  const mode: ProfileMode = focusOverride?.mode ?? raw.mode ?? 'full'
  const baseSections = mode === 'light' ? LIGHT_SECTIONS : FULL_SECTIONS
  const sections = mergeSections(
    mergeSections(baseSections, raw.sections),
    focusOverride?.sections,
  )

  return {
    enabled: true,
    mode,
    sections,
  }
}

function currentClientProfileConfig(): ProfileFeatureConfig | undefined {
  return (uiConfig as { features?: { profile?: ProfileFeatureConfig } }).features?.profile
}

/** Resolve profile page mode/sections for the active focus (current Vite client). */
export function resolveProfileFeature(focus?: string | null): ResolvedProfileFeature {
  return resolveProfileFeatureFromConfig(currentClientProfileConfig(), focus)
}

export function isProfileSectionEnabled(
  section: ProfileSectionKey,
  focus?: string | null,
): boolean {
  const feature = resolveProfileFeature(focus)
  if (!feature.enabled) return false
  return feature.sections[section] === true
}
